import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeaderSection from '../../components/layout/PageHeaderSection';
import PageLoadingShell from '../../components/layout/PageLoadingShell';
import Button from '../../components/common/Button';
import { TextInput } from '../../components/common/form';
import { getApiErrorInfo } from '../../utils/apiError';
import {
  getZoomSettings,
  saveZoomSettings,
  connectZoom,
  disconnectZoom,
  clearZoomCredentials,
} from '../../api/zoom';

/**
 * Admin Zoom connection — Server-to-Server OAuth.
 * Secrets never returned from the API (CONFIGURED sentinel).
 */
export default function ZoomConnectionPage() {
  const { t } = useTranslation();
  const errorRef = useRef(null);
  const [accountId, setAccountId] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [meetingSdkClientId, setMeetingSdkClientId] = useState('');
  const [meetingSdkClientSecret, setMeetingSdkClientSecret] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [loadFailed, setLoadFailed] = useState(false);

  const secretConfigured = settings?.clientSecret === 'CONFIGURED';
  const meetingSdkSecretConfigured = settings?.meetingSdkClientSecret === 'CONFIGURED';
  const webhookConfigured = settings?.webhookSecret === 'CONFIGURED';
  const connected = !!settings?.connected;
  const hasStoredCredentials = !!(settings?.configured || settings?.accountId || settings?.clientId || secretConfigured);
  const fieldsReady =
    !!accountId.trim() &&
    !!clientId.trim() &&
    (!!clientSecret.trim() || secretConfigured);
  const canConnect = !connecting && !saving && !clearing;

  const applySettings = (s) => {
    setSettings(s);
    setAccountId(s?.accountId || '');
    setClientId(s?.clientId || '');
    setClientSecret('');
    setMeetingSdkClientId(s?.meetingSdkClientId || '');
    setMeetingSdkClientSecret('');
    setWebhookSecret('');
  };

  const showError = (message) => {
    setError(message);
    requestAnimationFrame(() => {
      errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  };

  const load = async () => {
    setLoading(true);
    setError('');
    setLoadFailed(false);
    try {
      const s = await getZoomSettings();
      applySettings(s);
      try {
        localStorage.removeItem('classroom.zoom.connectionDraft');
      } catch {
        /* ignore */
      }
    } catch (err) {
      setLoadFailed(true);
      showError(getApiErrorInfo(err, t('classroom.zoom.loadFailed')).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const buildPayload = (overrides = {}) => ({
    enabled: settings?.enabled || false,
    accountId: accountId.trim(),
    clientId: clientId.trim(),
    clientSecret: clientSecret.trim()
      ? clientSecret.trim()
      : secretConfigured
        ? 'CONFIGURED'
        : '',
    meetingSdkClientId: meetingSdkClientId.trim(),
    meetingSdkClientSecret: meetingSdkClientSecret.trim()
      ? meetingSdkClientSecret.trim()
      : meetingSdkSecretConfigured
        ? 'CONFIGURED'
        : '',
    webhookSecret: webhookSecret.trim()
      ? webhookSecret.trim()
      : webhookConfigured
        ? 'CONFIGURED'
        : '',
    ...overrides,
  });

  const saveDraft = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    setError('');
    setNotice('');
    if (!accountId.trim() || !clientId.trim()) {
      showError(t('classroom.zoom.validationRequired'));
      setSaving(false);
      return;
    }
    if (!clientSecret.trim() && !secretConfigured) {
      showError(t('classroom.zoom.secretRequired'));
      setSaving(false);
      return;
    }
    try {
      const saved = await saveZoomSettings(buildPayload());
      applySettings(saved);
      setNotice(
        saved.connected
          ? t('classroom.zoom.draftSavedConnected')
          : t('classroom.zoom.draftSavedServer'),
      );
    } catch (err) {
      showError(getApiErrorInfo(err, t('classroom.zoom.draftSaveFailed')).message);
    } finally {
      setSaving(false);
    }
  };

  const handleConnect = async () => {
    setConnecting(true);
    setError('');
    setNotice('');
    if (!accountId.trim() || !clientId.trim()) {
      showError(t('classroom.zoom.validationRequired'));
      setConnecting(false);
      return;
    }
    if (!clientSecret.trim() && !secretConfigured) {
      showError(t('classroom.zoom.secretRequired'));
      setConnecting(false);
      return;
    }
    try {
      const result = await connectZoom(buildPayload({ enabled: true }));
      if (result?.settings) {
        applySettings(result.settings);
      }
      if (result?.success) {
        setNotice(t('classroom.zoom.connectSuccess'));
      } else {
        showError(t('classroom.zoom.connectFailed'));
      }
    } catch (err) {
      showError(getApiErrorInfo(err, t('classroom.zoom.connectFailed')).message);
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    setError('');
    setNotice('');
    try {
      const s = await disconnectZoom();
      applySettings(s);
      setNotice(t('classroom.zoom.disconnected'));
    } catch (err) {
      showError(getApiErrorInfo(err, t('classroom.zoom.disconnectFailed')).message);
    } finally {
      setDisconnecting(false);
    }
  };

  const handleClearCredentials = async () => {
    if (!window.confirm(t('classroom.zoom.clearConfirm'))) return;
    setClearing(true);
    setError('');
    setNotice('');
    try {
      const s = await clearZoomCredentials();
      applySettings(s);
      setNotice(t('classroom.zoom.cleared'));
    } catch (err) {
      showError(getApiErrorInfo(err, t('classroom.zoom.clearFailed')).message);
    } finally {
      setClearing(false);
    }
  };

  if (loading) {
    return (
      <PageLoadingShell
        variant="page-shell-form"
        title={t('classroom.zoom.title')}
        subtitle={t('classroom.zoom.subtitle')}
        backTo="/admin/classroom"
      />
    );
  }

  return (
    <div className="page-shell-form max-w-3xl">
      <PageHeaderSection
        eyebrow={t('nav.classroom')}
        title={t('classroom.zoom.title')}
        subtitle={t('classroom.zoom.subtitle')}
        showBack
        backTo="/admin/classroom"
      />

      <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
        {t('classroom.zoom.infoBanner')}
        {' '}
        <a
          href="https://marketplace.zoom.us/"
          target="_blank"
          rel="noreferrer"
          className="font-medium text-sky-700 underline hover:no-underline"
        >
          marketplace.zoom.us
        </a>
      </div>

      {loadFailed ? (
        <div
          ref={errorRef}
          className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
          role="alert"
        >
          <p>{error || t('classroom.zoom.loadFailed')}</p>
          <Button type="button" variant="page-secondary" className="mt-2" onClick={() => load()}>
            {t('common.retry', { defaultValue: 'Retry' })}
          </Button>
        </div>
      ) : (
        <>
      <div
        className={`mb-6 rounded-xl border px-4 py-3 text-sm ${
          connected
            ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
            : 'border-amber-200 bg-amber-50 text-amber-950'
        }`}
        role="status"
      >
        <strong>{connected ? t('classroom.zoom.statusConnected') : t('classroom.zoom.statusNotConnected')}</strong>
        <p className="mt-1">
          {connected ? t('classroom.zoom.statusConnectedHint') : t('classroom.zoom.statusNotConnectedHintLive')}
        </p>
        {!connected && !error && settings?.lastError && !/required to (connect|save)/i.test(settings.lastError) && (
          <p className="mt-2 text-amber-900/90">{settings.lastError}</p>
        )}
      </div>

      {notice && (
        <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900" role="status">
          {notice}
        </div>
      )}
      {error && (
        <div
          ref={errorRef}
          className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
          role="alert"
        >
          {error}
        </div>
      )}

      <form onSubmit={saveDraft} className="space-y-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-base font-semibold text-slate-900">{t('classroom.zoom.serverToServerHeading')}</h2>
        <p className="text-sm text-slate-600">{t('classroom.zoom.serverToServerHintLive')}</p>

        <TextInput
          label={t('classroom.zoom.accountId')}
          name="zoomAccountId"
          value={accountId}
          onChange={(e) => setAccountId(e.target.value)}
          placeholder={t('classroom.zoom.clientIdPlaceholder', { defaultValue: 'e.g. AbCdEfGh...' })}
          note={t('classroom.zoom.accountIdNote')}
          autoComplete="off"
        />
        <TextInput
          label={t('classroom.zoom.clientId')}
          name="zoomClientId"
          value={clientId}
          onChange={(e) => setClientId(e.target.value)}
          autoComplete="off"
        />
        <TextInput
          label={t('classroom.zoom.clientSecret')}
          name="zoomClientSecret"
          type="password"
          value={clientSecret}
          onChange={(e) => setClientSecret(e.target.value)}
          note={
            secretConfigured
              ? t('classroom.zoom.clientSecretConfiguredNote')
              : t('classroom.zoom.clientSecretConnectNote')
          }
          placeholder={secretConfigured ? '••••••••' : undefined}
          autoComplete="off"
        />

        <div className="border-t border-slate-100 pt-4 mt-2 space-y-4">
          <h3 className="text-sm font-semibold text-slate-900">{t('classroom.zoom.meetingSdkHeading')}</h3>
          <p className="text-sm text-slate-600">{t('classroom.zoom.meetingSdkHint')}</p>
          <TextInput
            label={t('classroom.zoom.meetingSdkClientId')}
            name="meetingSdkClientId"
            value={meetingSdkClientId}
            onChange={(e) => setMeetingSdkClientId(e.target.value)}
            note={t('classroom.zoom.meetingSdkClientIdNote')}
            autoComplete="off"
          />
          <TextInput
            label={t('classroom.zoom.meetingSdkClientSecret')}
            name="meetingSdkClientSecret"
            type="password"
            value={meetingSdkClientSecret}
            onChange={(e) => setMeetingSdkClientSecret(e.target.value)}
            note={
              meetingSdkSecretConfigured
                ? t('classroom.zoom.meetingSdkClientSecretConfiguredNote')
                : t('classroom.zoom.meetingSdkClientSecretNote')
            }
            placeholder={meetingSdkSecretConfigured ? '••••••••' : undefined}
            autoComplete="off"
          />
        </div>

        <div className="border-t border-slate-100 pt-4 mt-2">
          <h3 className="text-sm font-semibold text-slate-900">{t('classroom.zoom.webhookHeading')}</h3>
          <p className="mt-1 text-sm text-slate-600">{t('classroom.zoom.webhookHint')}</p>
          {settings?.webhookUrl && (
            <p className="mt-2 rounded-lg bg-slate-50 px-3 py-2 font-mono text-xs text-slate-700 break-all">
              {settings.webhookUrl}
            </p>
          )}
          <div className="mt-3">
            <TextInput
              label={t('classroom.zoom.webhookSecret')}
            name="zoomWebhookSecret"
            type="password"
            value={webhookSecret}
            onChange={(e) => setWebhookSecret(e.target.value)}
            note={
              webhookConfigured
                ? t('classroom.zoom.webhookSecretConfiguredNote')
                : t('classroom.zoom.webhookSecretNote')
            }
            placeholder={webhookConfigured ? '••••••••' : undefined}
            autoComplete="off"
          />
          </div>
        </div>

        <div className="flex flex-wrap gap-2 pt-2">
          <Button type="submit" variant="page-secondary" disabled={saving || connecting || clearing}>
            {saving ? t('classroom.zoom.saving') : t('classroom.zoom.saveCredentials')}
          </Button>
          <Button
            type="button"
            variant="page-primary"
            disabled={!canConnect}
            title={!fieldsReady ? t('classroom.zoom.connectNeedsFields') : undefined}
            onClick={handleConnect}
          >
            {connecting ? t('classroom.zoom.connecting') : t('classroom.zoom.connectOAuth')}
          </Button>
          {connected && (
            <Button
              type="button"
              variant="page-secondary"
              disabled={disconnecting || connecting || clearing}
              onClick={handleDisconnect}
            >
              {disconnecting ? t('classroom.zoom.disconnecting') : t('classroom.zoom.disconnect')}
            </Button>
          )}
          {hasStoredCredentials && (
            <Button
              type="button"
              variant="page-secondary"
              disabled={clearing || connecting || saving}
              onClick={handleClearCredentials}
            >
              {clearing ? t('classroom.zoom.clearing') : t('classroom.zoom.clearCredentials')}
            </Button>
          )}
        </div>
        <p className="text-xs text-slate-500">
          {fieldsReady ? t('classroom.zoom.connectHintLive') : t('classroom.zoom.connectNeedsFields')}
        </p>
      </form>

      <p className="mt-6 text-sm text-slate-600">
        <Link to="/admin/classroom/settings" className="font-medium text-sky-700 hover:underline">
          {t('classroom.zoom.gotoSettings')}
        </Link>
      </p>
        </>
      )}
    </div>
  );
}
